import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { runsApi } from "../api/endpoints";
import { SkeletonCard } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { AuditStrip } from "../components/audit/AuditStrip";
import { FeasibilityBadge } from "../components/chips/FeasibilityBadge";
import { SeverityChip } from "../components/chips/SeverityChip";
import { FactChip } from "../components/chips/FactChip";
import { ConfidenceChip } from "../components/chips/ConfidenceChip";
import { ProvenancePopover } from "../components/provenance/ProvenancePopover";
import { StrategyOptionsPanel } from "../components/ai/StrategyOptionsPanel";
import { FindingCard } from "./RunDetail";
import {
  DIMENSION_ORDER,
  DIMENSION_LABELS,
  DIMENSION_STATUS_META,
} from "../lib/constants";
import { formatMinutes } from "../lib/format";
import { cn } from "../lib/utils";
import { useEngineMeta } from "../components/shell/AppShell";

/**
 * ProcessDetail — the star page.
 *
 * Three concepts kept STRUCTURALLY separate (see PRD §6):
 *   1. BIA Quality       (findings count / severities)
 *   2. Recovery Feasibility (engine overall_status)
 *   3. Validation Confidence (feasibility_confidence + adjustments verbatim)
 *
 * Nothing in the UI implies "3 quality findings → NOT_FEASIBLE" or vice
 * versa. The three panels sit side by side; each reads from its own
 * backend field, never derived from the others.
 */
export default function ProcessDetailPage() {
  const { workspaceId, runId, processId } = useParams();
  const [proc, setProc] = useState(null);
  const [findings, setFindings] = useState(null);
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const { setEngineMeta } = useEngineMeta();

  useEffect(() => {
    let mounted = true;
    setError(null);
    Promise.all([
      runsApi.processDetail(workspaceId, runId, processId),
      runsApi.findings(workspaceId, runId, { process_id: processId, page_size: 200 }),
      runsApi.get(workspaceId, runId),
    ])
      .then(([p, f, r]) => {
        if (!mounted) return;
        setProc(p);
        setFindings(f);
        setRun(r);
        setEngineMeta({
          engine_version: r.engine_version,
          rule_library_version: r.rule_library_version,
          rule_library_hash: r.rule_library_hash,
        });
      })
      .catch((e) => mounted && setError(e));
    return () => {
      mounted = false;
      setEngineMeta(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, runId, processId]);

  if (error) {
    return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  }
  if (!proc || !run) return <SkeletonCard />;

  const items = findings?.items || [];
  const qualityCounts = tallySeverity(items);
  const qualityTotal = items.length;

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          <Link
            to={`/w/${workspaceId}/analyses`}
            className="hover:text-slate-900"
          >
            Analyses
          </Link>{" "}
          ·{" "}
          <Link
            to={`/w/${workspaceId}/analyses/${runId}`}
            className="hover:text-slate-900"
          >
            {run.name}
          </Link>
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1
              data-testid="process-detail-name"
              className="text-2xl font-semibold text-slate-900"
            >
              {proc.process_name || proc.process_id}
            </h1>
            <div className="mt-1 text-xs font-mono text-slate-500">
              {proc.process_id}
            </div>
          </div>
        </div>
        <AuditStrip run={run} />
      </header>

      {/* ----- THREE SEPARATE PANELS (BQ · Feasibility · Confidence) ------ */}
      <section
        data-testid="three-concepts"
        className="grid gap-4 md:grid-cols-3"
      >
        <ConceptCard
          testId="concept-bia-quality"
          eyebrow="BIA Quality"
          eyebrowTip="Count of triggered engine quality findings (BQ-###). Quality is separate from feasibility."
        >
          <div className="flex items-baseline gap-2">
            <div
              className="text-3xl font-semibold text-slate-900"
              data-testid="bq-total"
            >
              {qualityTotal}
            </div>
            <div className="text-xs text-slate-500">findings triggered</div>
          </div>
          <ul className="mt-3 space-y-1 text-xs">
            {["CRITICAL", "HIGH", "MEDIUM", "LOW", "REVIEW"].map((s) => (
              <li
                key={s}
                className="flex items-center justify-between"
                data-testid={`bq-count-${s.toLowerCase()}`}
              >
                <SeverityChip severity={s} />
                <span className="font-mono text-slate-800">
                  {qualityCounts[s] || 0}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Quality findings never by themselves change feasibility. They may
            lower confidence.
          </p>
        </ConceptCard>

        <ConceptCard
          testId="concept-feasibility"
          eyebrow="Recovery Feasibility"
          eyebrowTip="Engine-derived overall recovery-feasibility status for this process (deterministic — no AI)."
        >
          <FeasibilityBadge status={proc.overall_status} size="lg" />
          <div className="mt-3 text-xs text-slate-600 leading-relaxed">
            Data completeness:{" "}
            <span
              className="font-mono text-slate-900"
              data-testid="data-completeness-pct"
            >
              {proc.data_completeness_pct === null ||
              proc.data_completeness_pct === undefined
                ? "—"
                : `${Math.round(proc.data_completeness_pct)}%`}
            </span>
          </div>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Derived from the nine dimensions below plus the workaround-bridge
            rule (Case I). See the derivation trace.
          </p>
        </ConceptCard>

        <ConceptCard
          testId="concept-confidence"
          eyebrow="Validation Confidence"
          eyebrowTip="How strongly the feasibility conclusion is supported by data completeness, recent test evidence, and dependency data."
        >
          <ConfidenceChip
            level={proc.feasibility_confidence}
            reasons={proc.confidence_adjustments || []}
          />
          <div className="mt-3 text-xs text-slate-600 space-y-1">
            {(proc.confidence_adjustments || []).length === 0 && (
              <div className="italic">No confidence adjustments recorded.</div>
            )}
            {(proc.confidence_adjustments || []).slice(0, 5).map((r, i) => (
              <div
                key={i}
                data-testid={`conf-adj-${i}`}
                className="font-mono text-slate-700"
              >
                <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px]">
                  {r.rule_id}
                </code>{" "}
                {r.from_level} → {r.to_level}
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Confidence is orthogonal to feasibility. Stale BIA (BQ-010) or
            duplicate patterns lower confidence, not feasibility.
          </p>
        </ConceptCard>
      </section>

      {/* ----- Process facts strip (with provenance affordances) ---------- */}
      <ProcessFactsStrip
        proc={proc}
        workspaceId={workspaceId}
        runId={runId}
        processId={processId}
      />

      {/* ----- Dimensions -------------------------------------------------- */}
      <section aria-labelledby="dims-heading" className="space-y-3">
        <h2 id="dims-heading" className="text-sm font-semibold text-slate-900">
          Recovery feasibility dimensions
        </h2>
        <div className="space-y-2" data-testid="dimensions-list">
          {DIMENSION_ORDER.map((name) => (
            <DimensionRow
              key={name}
              name={name}
              dim={proc.per_dimension?.[name]}
              workspaceId={workspaceId}
              runId={runId}
              processId={processId}
            />
          ))}
        </div>
      </section>

      {/* ----- Derivation trace ------------------------------------------- */}
      <section aria-labelledby="trace-heading">
        <h2
          id="trace-heading"
          className="text-sm font-semibold text-slate-900 mb-2"
        >
          Derivation trace
        </h2>
        <div
          data-testid="derivation-trace"
          className="rounded border border-slate-200 bg-white p-4 text-xs font-mono text-slate-800 space-y-1"
        >
          <div className="text-slate-500">
            overall_status ={" "}
            <span className="text-slate-900">
              {proc.derivation_trace?.overall_status || proc.overall_status}
            </span>
          </div>
          {proc.derivation_trace?.workaround_bridge_applied && (
            <div
              className="text-emerald-700"
              data-testid="workaround-bridge-flag"
            >
              workaround_bridge_applied = true
            </div>
          )}
          {(proc.derivation_trace?.steps || []).map((s, i) => (
            <div key={i} className="text-slate-700">
              · {s}
            </div>
          ))}
          {(proc.derivation_trace?.triggered_conditions || []).map((c, i) => (
            <div key={i} className="text-amber-800">
              → {c}
            </div>
          ))}
        </div>
      </section>

      {/* ----- Findings for this process ---------------------------------- */}
      <section aria-labelledby="findings-heading">
        <h2
          id="findings-heading"
          className="text-sm font-semibold text-slate-900 mb-2"
        >
          Findings for this process
        </h2>
        {items.length === 0 ? (
          <EmptyState title="No findings triggered for this process." />
        ) : (
          <ul className="space-y-3" data-testid="process-findings">
            {items.map((f) => (
              <FindingCard
                key={f.id}
                finding={f}
                workspaceId={workspaceId}
                runId={runId}
              />
            ))}
          </ul>
        )}
      </section>

      {/* ----- Recovery Strategy Options (Phase 6c) ------------------------ */}
      <StrategyOptionsPanel
        workspaceId={workspaceId}
        runId={runId}
        processId={processId}
        processName={proc?.process_name || proc?.process_id || processId}
      />
    </div>
  );
}

function ConceptCard({ eyebrow, eyebrowTip, testId, children }) {
  return (
    <div
      data-testid={testId}
      className="rounded border border-slate-200 bg-white p-4"
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
        <span title={eyebrowTip}>{eyebrow}</span>
      </div>
      {children}
    </div>
  );
}

const KNOWN_FIELDS_BY_DIM = {
  // Retained for future dimension-scoped provenance drawers; the
  // ProcessFactsStrip currently owns rendering of these four cardinal
  // process-level facts.
  TIME: ["rto_minutes", "mtpd_minutes"],
  DATA: ["rpo_minutes"],
  CAPACITY: ["mbco_pct"],
};

// Compact, prominent strip that surfaces the four material process-level
// facts (RTO / RPO / MTPD / MBCO) with a provenance affordance on each one.
// These are the numbers that most reviewers reach for first; making the
// provenance one click away enforces the "SOURCE FACT" trust model.
function ProcessFactsStrip({ proc, workspaceId, runId, processId }) {
  const time = proc.per_dimension?.TIME?.evidence || {};
  const data = proc.per_dimension?.DATA?.evidence || {};
  const cap = proc.per_dimension?.CAPACITY?.evidence || {};
  const facts = [
    {
      key: "rto_minutes",
      label: "RTO",
      value: time.rto_minutes,
      render: formatMinutes,
    },
    {
      key: "rpo_minutes",
      label: "RPO",
      value: data.rpo_minutes,
      render: formatMinutes,
    },
    {
      key: "mtpd_minutes",
      label: "MTPD",
      value: time.mtpd_minutes,
      render: formatMinutes,
    },
    {
      key: "mbco_pct",
      label: "MBCO",
      value: cap.mbco_pct,
      render: (v) =>
        v === null || v === undefined ? "—" : `${Math.round(v)}%`,
    },
  ];
  return (
    <section
      aria-labelledby="process-facts-heading"
      data-testid="process-facts-strip"
      className="rounded border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <h2
          id="process-facts-heading"
          className="text-sm font-semibold text-slate-900"
        >
          Process facts
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          each fact carries its own provenance
        </span>
      </div>
      <dl className="grid gap-4 sm:grid-cols-4">
        {facts.map((f) => (
          <div key={f.key} data-testid={`fact-cell-${f.key}`}>
            <dt className="flex items-center text-[10px] uppercase tracking-widest text-slate-500">
              <FactChip source="SOURCE_FACT" />
              <span className="ml-1.5">{f.label}</span>
            </dt>
            <dd className="mt-1 flex items-center gap-1">
              <span
                className="text-lg font-mono text-slate-900"
                data-testid={`fact-value-${f.key}`}
              >
                {f.render(f.value)}
              </span>
              <ProvenancePopover
                workspaceId={workspaceId}
                runId={runId}
                processId={processId}
                field={f.key}
              />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function DimensionRow({ name, dim, workspaceId, runId, processId }) {
  const [open, setOpen] = useState(false);
  const status = dim?.status || "UNKNOWN";
  const meta = DIMENSION_STATUS_META[status] || DIMENSION_STATUS_META.UNKNOWN;
  const bridged = !!dim?.evidence?.bridged_by_workaround;

  return (
    <div
      data-testid={`dim-row-${name}`}
      className="rounded border border-slate-200 bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`dim-toggle-${name}`}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-medium text-slate-900 truncate">
            {DIMENSION_LABELS[name]}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {bridged && (
            <span
              className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800"
              data-testid={`dim-bridged-${name}`}
              title="TECHNOLOGY gap bridged by a tested workaround (Case I)."
            >
              bridged
            </span>
          )}
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-mono",
              meta.text,
              "bg-slate-100",
            )}
            data-testid={`dim-status-${name}`}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
            {meta.label}
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 text-xs">
          {dim?.notes && dim.notes.length > 0 && (
            <ul className="mb-3 list-disc pl-4 text-slate-700 space-y-0.5">
              {dim.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
          {name === "TECHNOLOGY" && dim?.evidence?.evaluated && (
            <TechEvidence
              rows={dim.evidence.evaluated}
              gap={dim.gap}
              workspaceId={workspaceId}
              runId={runId}
              processId={processId}
            />
          )}
          {name === "CAPACITY" && dim?.evidence?.sources && (
            <CapacityEvidence evidence={dim.evidence} />
          )}
          <details>
            <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
              Evidence JSON
            </summary>
            <pre className="mt-2 rounded bg-white border border-slate-200 p-3 text-[11px] font-mono text-slate-800 overflow-x-auto">
              {JSON.stringify(
                {
                  evidence: dim?.evidence || {},
                  gap: dim?.gap || null,
                },
                null,
                2,
              )}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function TechEvidence({ rows, gap, workspaceId, runId, processId }) {
  const claimedGaps = new Set((gap?.claimed_gaps || []).map((r) => r.id));
  const demoGaps = new Set((gap?.demonstrated_gaps || []).map((r) => r.id));
  return (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="tech-evidence-table">
        <thead className="text-left text-slate-500">
          <tr>
            <th className="pb-1">System</th>
            <th className="pb-1">Required</th>
            <th className="pb-1">Claimed RTA</th>
            <th className="pb-1">Demonstrated RTA</th>
          </tr>
        </thead>
        <tbody className="text-slate-800 font-mono">
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-200">
              <td className="py-1">{r.system_name}</td>
              <TechCell
                testId={`tech-required-${r.id}`}
                value={r.required_recovery_minutes}
                field={`Applications.${r.id}.required_recovery_minutes`}
                workspaceId={workspaceId}
                runId={runId}
                processId={processId}
              />
              <TechCell
                testId={`tech-claimed-${r.id}`}
                value={r.claimed_rta_minutes}
                field={`Applications.${r.id}.claimed_rta_minutes`}
                workspaceId={workspaceId}
                runId={runId}
                processId={processId}
                bad={claimedGaps.has(r.id)}
              />
              <TechCell
                testId={`tech-demonstrated-${r.id}`}
                value={r.demonstrated_rta_minutes}
                field={`Applications.${r.id}.demonstrated_rta_minutes`}
                workspaceId={workspaceId}
                runId={runId}
                processId={processId}
                bad={demoGaps.has(r.id)}
              />
            </tr>
          ))}
        </tbody>
      </table>
      {(claimedGaps.size > 0 || demoGaps.size > 0) && (
        <div className="mt-2 text-[11px] text-slate-500">
          Claimed and Demonstrated shown side by side so a "claimed passes but
          demonstrated fails" case is never hidden.
        </div>
      )}
    </div>
  );
}

function TechCell({ value, field, testId, bad, workspaceId, runId, processId }) {
  return (
    <td
      className={cn("py-1", bad && "text-red-700 font-semibold")}
      data-testid={testId}
    >
      <span className="inline-flex items-center">
        <span>{formatMinutes(value)}</span>
        <ProvenancePopover
          workspaceId={workspaceId}
          runId={runId}
          processId={processId}
          field={field}
        />
      </span>
    </td>
  );
}

function CapacityEvidence({ evidence }) {
  const { mbco_pct, sources = {}, best_source, best_value } = evidence;
  return (
    <div className="mb-3 text-[11px]">
      <div className="text-slate-700">
        MBCO target: <code className="font-mono">{mbco_pct ?? "—"}%</code>
      </div>
      <ul className="mt-1 space-y-0.5">
        {Object.entries(sources).map(([k, v]) => (
          <li key={k} className="font-mono">
            <span className="text-slate-500">{k}:</span>{" "}
            <span
              className={cn(
                "text-slate-800",
                k === best_source && "font-semibold",
              )}
            >
              {v === null || v === undefined ? "—" : `${v}%`}
            </span>
          </li>
        ))}
      </ul>
      {best_value !== undefined && (
        <div className="mt-1 text-slate-600">
          Best capacity source: <code className="font-mono">{best_source}</code>{" "}
          @ {best_value}%
        </div>
      )}
    </div>
  );
}

function tallySeverity(items) {
  const acc = {};
  for (const f of items) {
    const s = f.effective_severity;
    if (!s) continue;
    acc[s] = (acc[s] || 0) + 1;
  }
  return acc;
}
